import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ShippingCaseInsurances, CreateInsuranceFields } from "./types";
import { AppThunk } from "@store/index";

import {
  createInsuranceApi,
  getInsurnceApi,
  removeInsuranceApi,
  updateInsuranceApi,
} from "./api";

type InsuranceState = {
  items: ShippingCaseInsurances;
  loading: boolean;
  error: string | null;
  success: string | null;
};

const initialState: InsuranceState = {
  items: [],
  loading: false,
  error: null,
  success: null,
};

const insuranceSlice = createSlice({
  name: "insurance",
  initialState,
  reducers: {
    getStart(state: InsuranceState) {
      state.loading = true;
      state.error = null;
    },
    getSuccess(state: InsuranceState, action: PayloadAction<{ items: any }>) {
      const { items } = action.payload;
      state.items = items;
      state.loading = false;
      state.error = null;
    },
    createSuccess(
      state: InsuranceState,
      action: PayloadAction<{ success: string }>,
    ) {
      const { success } = action.payload;
      state.success = success;
      state.loading = false;
      state.error = null;
    },
    getFailure(
      state: InsuranceState,
      action: PayloadAction<{ error: string }>,
    ) {
      const { error } = action.payload;
      state.loading = false;
      state.error = error;
    },
    cleanData(state: InsuranceState) {
      state.items = [];
    },
    hideWarning(state: InsuranceState) {
      state.error = null;
      state.success = null;
    },
  },
});

export const {
  getStart,
  getSuccess,
  getFailure,
  cleanData,
  createSuccess,
  hideWarning,
} = insuranceSlice.actions;

export const { actions, reducer } = insuranceSlice;
export default insuranceSlice.reducer;

export const fetchInsurance =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart());
      const { items } = await getInsurnceApi(id);
      dispatch(getSuccess({ items }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ error }));
    }
  };

export const createInsurance =
  (params: CreateInsuranceFields, id: number): AppThunk =>
  async (dispatch) => {
    try {
      await createInsuranceApi({ params, id });
      dispatch(
        createSuccess({
          success: "Insurance was created successfully",
        }),
      );
      dispatch(fetchInsurance(id));
    } catch {
      dispatch(
        getFailure({
          error: "Shipping case creation failed",
        }),
      );
    }
  };

export const removeInsurance =
  (id: number, insurance_id: number): AppThunk =>
  async (dispatch) => {
    try {
      await removeInsuranceApi(id, insurance_id);
      dispatch(
        createSuccess({
          success: "Insurance was removed successfully",
        }),
      );
      dispatch(fetchInsurance(id));
    } catch {
      dispatch(
        getFailure({
          error: "Insurance removing failed",
        }),
      );
    }
  };

export const updateInsurance =
  (params: CreateInsuranceFields, id: number, insurance_id: number): AppThunk =>
  async (dispatch) => {
    try {
      await updateInsuranceApi(params, id, insurance_id);
      dispatch(
        createSuccess({
          success: "Insurance was updated successfully",
        }),
      );
      dispatch(fetchInsurance(id));
    } catch {
      dispatch(
        getFailure({
          error: "Insurance update failed",
        }),
      );
    }
  };
